import React from 'react';
import { st, classes } from './Body.st.css';
import { MainComponents } from '../../types';
import { RenderLocationProvider } from '../useRenderLocation';
import {
  SectionNotification,
  SectionNotificationType as NOTIFICATION_TYPE,
} from 'wix-ui-tpa/cssVars';
import {
  AVAILABILITY_MESSAGE_TYPE,
  BodyViewModel,
} from '../../../../service-page-view-model/body-view-model/bodyViewModel';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams, { withoutDefaultFallback } from '../../settingsParams';
import {
  useTranslation,
  useEnvironment,
  useExperiments,
} from '@wix/yoshi-flow-editor';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { COURSES_EXPERIMENT } from '../../constants';

type BodyProps = {
  viewModel: BodyViewModel;
  className?: string;
};

const BodyComponent: React.FC<BodyProps> = ({
  viewModel,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const { isRTL } = useEnvironment();
  const { experiments } = useExperiments();
  const isCoursesEnabled = experiments.enabled(COURSES_EXPERIMENT);
  /**
   * What the owner typed, or undefined when they never touched the field.
   * `settings.get` folds the published default in, so an untouched field and
   * an owner who typed that same sentence by hand come back identical.
   * `withoutDefaultFallback` drops the default, leaving only the raw value.
   */
  const ownerText = (settingsParam: typeof settingsParams.bookButtonText) =>
    settings.get(withoutDefaultFallback(settingsParam)) || undefined;

  const getMessageText = (messageType: AVAILABILITY_MESSAGE_TYPE) => {
    switch (messageType) {
      case AVAILABILITY_MESSAGE_TYPE.FULLY_BOOKED:
        return (
          settings.get(settingsParams.fullCourseMessageText) ||
          settings.getDefaultValue(settingsParams.fullCourseMessageText)
        );
      case AVAILABILITY_MESSAGE_TYPE.TOO_EARLY:
        // The date, not a relative time (Figma 4235-117128). Without a date
        // the sentence would end in "open on ." - worse than the old wording,
        // so the relative one stands in.
        return isCoursesEnabled && viewModel.enrollmentOpenDate
          ? t('app.widget.uou-messages.course-not-open-yet', {
              date: viewModel.enrollmentOpenDate,
            })
          : t('app.widget.uou-messages.book-is-not-open-yet', {
              timeUntilServiceIsOpen: viewModel.timeUntilServiceIsOpen,
            });
      case AVAILABILITY_MESSAGE_TYPE.STARTED_AND_BOOKABLE:
        return (
          settings.get(settingsParams.bookStartedCourseMessageText) ||
          settings.getDefaultValue(settingsParams.bookStartedCourseMessageText)
        );
      case AVAILABILITY_MESSAGE_TYPE.TOO_LATE:
        // The owner's own text still wins. Only the DEFAULT changes, to the
        // longer sentence that points the visitor at the business instead of
        // leaving them with a dead end (Figma 4235-117128).
        return (
          ownerText(settingsParams.unBookableCourseMessageText) ||
          (isCoursesEnabled
            ? t('app.widget.uou-messages.course-closed-contact-us')
            : settings.getDefaultValue(
                settingsParams.unBookableCourseMessageText,
              ))
        );
      case AVAILABILITY_MESSAGE_TYPE.NOT_AVAILABLE:
      default:
        return (
          settings.get(settingsParams.serviceUnavailableMessageText) ||
          settings.getDefaultValue(settingsParams.serviceUnavailableMessageText)
        );
    }
  };
  return (
    <RenderLocationProvider value={MainComponents.BODY}>
      <section
        aria-label={t('body-aria-label.service-name-details', {
          serviceName: viewModel.serviceName,
        })}
        className={st(
          classes.root,
          {
            alignment: settings.get(settingsParams.bodyAlignment),
            columnAlignment: settings.get(settingsParams.columnPosition),
            isRTL,
          },
          className,
        )}
        data-hook="body"
      >
        <div className={classes.columnWrapper}>
          {viewModel.breadcrumbsViewModel && (
            <div className={classes.breadcrumbsWrapper}>
              <Breadcrumbs
                breadcrumbsViewModel={viewModel.breadcrumbsViewModel}
              />
            </div>
          )}
          {viewModel.messageType && (
            <div
              role="status"
              data-hook="no-book-button-message"
              className={classes.notificationBanner}
            >
              <SectionNotification
                type={NOTIFICATION_TYPE.wired}
                className={st(classes.sectionNotification)}
              >
                <SectionNotification.Text>
                  {getMessageText(viewModel.messageType)}
                </SectionNotification.Text>
              </SectionNotification>
            </div>
          )}
          <div data-hook="body-children-wrapper">{children}</div>
        </div>
      </section>
    </RenderLocationProvider>
  );
};

export default BodyComponent;
